import React from 'react';

import Button from '../../../shared-components/Button';
import styles from './OfflineMode.module.scss';
import { ACTIVATION_FACTS, ACTIVATION_FACTS_HEADING, DORMANT, orgLabel } from './copy';
import { formatLastActivation } from './formatting';
import type { OfflineModeActivationWindow } from 'redux-stores/offlineMode';

export type DormantPanelProps = {
  organizationName: string;
  lastActivation: OfflineModeActivationWindow | null;
  isSwitching: boolean;
  onActivate: () => void;
  onSendReturnBroadcast: () => void;
  broadcast?: React.ReactNode;
  eventLog?: React.ReactNode;
};

export default function DormantPanel({
  organizationName,
  lastActivation,
  isSwitching,
  onActivate,
  onSendReturnBroadcast,
  broadcast,
  eventLog,
}: DormantPanelProps) {
  const org = orgLabel(organizationName);
  const lastActivationText = formatLastActivation(lastActivation);

  return (
    <section className={styles.panel} data-test-id="offlineModeDormantPanel">
      <div className={styles.statusPanel}>
        <span className={styles.iconWell} aria-hidden="true">
          <span className={styles.iconDisc} />
        </span>

        <div className={styles.statusMain}>
          <h3 className={styles.statusTitle} data-test-id="offlineModeStatus">
            {DORMANT.status}
          </h3>

          <p className={styles.statusBody} data-test-id="offlineModeOrgName">
            {org} — Offline Mode organization
          </p>
          <p className={styles.statusBody} data-test-id="offlineModeAvailability">
            {DORMANT.availability}
          </p>

          {lastActivationText ? (
            <p className={styles.statusMeta} data-test-id="offlineModeLastActivation">
              {DORMANT.lastActivationLabel}: {lastActivationText}
            </p>
          ) : null}

          <p className={styles.statusHint} data-test-id="offlineModeActivateHint">
            {DORMANT.activateHint}
          </p>
        </div>

        <div className={styles.statusActions}>
          <Button
            variant="action"
            size="medium"
            className={styles.statusActionButton}
            data-test-id="offlineModeActivateButton"
            disabled={isSwitching}
            onClick={onActivate}
          >
            Activate Offline Mode
          </Button>
          {lastActivation ? (
            <Button
              variant="outlined"
              size="medium"
              className={styles.statusActionButton}
              data-test-id="offlineModeSendReturnBroadcastButton"
              disabled={isSwitching}
              onClick={onSendReturnBroadcast}
            >
              {DORMANT.sendReturnBroadcast}
            </Button>
          ) : null}
        </div>
      </div>

      <h4 className={styles.sectionHeading}>{ACTIVATION_FACTS_HEADING}</h4>
      <ul className={styles.facts} data-test-id="offlineModeActivationFacts">
        {ACTIVATION_FACTS.map((fact) => (
          <li className={styles.fact} key={fact}>
            <span className={styles.factMarker} aria-hidden="true">
              <span className={styles.factMarkerDot} />
            </span>
            <p className={styles.factText}>{fact}</p>
          </li>
        ))}
      </ul>

      {broadcast ? (
        <section className={styles.subSection} data-test-id="offlineModeBroadcastSlot">
          {broadcast}
        </section>
      ) : null}

      {eventLog ? <div data-test-id="offlineModeEventLogSlot">{eventLog}</div> : null}
    </section>
  );
}
